import {
  BOX_LIST_DEFAULTS,
  BOX_LIST_SORTS,
  BOX_PRICE_RANGES,
  type BoxListSort,
  type BoxPriceRange,
  VOLATILITY_MAX,
} from "@loot-os/api-contract";
import type { BoxListFilters } from "@loot-os/design-system/templates/shared/content";

/**
 * A boxes list's filters as its URL carries them (the catalog's and the Forge's), so a filtered
 * view survives opening a box and coming back. A filter at its default is left out of the URL.
 * The routes read it before any page loads, so it imports nothing but api-contract's data.
 */
export interface BoxListSearch {
  q?: string;
  price?: Exclude<BoxPriceRange, "all">;
  sort?: Exclude<BoxListSort, "popular">;
  /** The volatility range's ends, left out at the scale's own. */
  vmin?: number;
  vmax?: number;
}

function isMember<T extends string>(set: readonly T[], value: unknown): value is T {
  return typeof value === "string" && (set as readonly string[]).includes(value);
}

/** A point on the volatility scale, or nothing: a whole number from 0 to its top. */
function scalePoint(value: unknown): number | undefined {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= VOLATILITY_MAX
    ? n
    : undefined;
}

/** A route's search, read for the list's filters: anything unrecognized is dropped. */
export function parseBoxListSearch(search: Record<string, unknown>): BoxListSearch {
  const q = typeof search.q === "string" && search.q !== "" ? search.q : undefined;
  const price =
    isMember(BOX_PRICE_RANGES, search.price) && search.price !== "all" ? search.price : undefined;
  const sort =
    isMember(BOX_LIST_SORTS, search.sort) && search.sort !== "popular" ? search.sort : undefined;
  const vmin = scalePoint(search.vmin);
  const vmax = scalePoint(search.vmax);
  return {
    ...(q !== undefined ? { q } : {}),
    ...(price ? { price } : {}),
    ...(sort ? { sort } : {}),
    ...(vmin !== undefined && vmin > 0 ? { vmin } : {}),
    ...(vmax !== undefined && vmax < VOLATILITY_MAX ? { vmax } : {}),
  };
}

/**
 * The filters the URL holds, the defaults filling what it leaves out. Favorites and Within balance
 * need a shopper: signed out (a shared link) they read as their defaults, so the list shows every
 * box rather than an empty or waiting one, and the fields show what is applied.
 */
export function boxListFiltersOf(
  search: BoxListSearch,
  { signedIn }: { signedIn: boolean },
): BoxListFilters {
  const reset = BOX_LIST_DEFAULTS;
  const price = search.price ?? reset.price;
  const sort = search.sort ?? reset.sort;
  return {
    query: search.q ?? reset.query,
    price: price === "afford" && !signedIn ? reset.price : price,
    sort: sort === "favorites" && !signedIn ? reset.sort : sort,
    volatility: [search.vmin ?? 0, search.vmax ?? VOLATILITY_MAX],
  };
}

/** Every entry of a list's URL, each set or cleared (`undefined`). */
export interface BoxListSearchPatch {
  q: string | undefined;
  price: BoxListSearch["price"] | undefined;
  sort: BoxListSearch["sort"] | undefined;
  vmin: number | undefined;
  vmax: number | undefined;
}

/** The URL's entries for a value of the filters: each set to it, or cleared at its default. */
export function boxListSearchOf(value: BoxListFilters): BoxListSearchPatch {
  const [vmin, vmax] = value.volatility;
  return {
    q: value.query === "" ? undefined : value.query,
    price: value.price === "all" ? undefined : value.price,
    sort: value.sort === "popular" ? undefined : value.sort,
    vmin: vmin === undefined || vmin <= 0 ? undefined : vmin,
    vmax: vmax === undefined || vmax >= VOLATILITY_MAX ? undefined : vmax,
  };
}
